package org.teddapp.project.ui.finance

import org.teddapp.project.ui.TeddSize

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import com.tedd.db.Expense_list
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.TeddTokens

internal data class ListDraft(
    val name: String,
    val description: String?,
    val status: String, // "active" | "planned"
    val date: String?,
    val recurrence: String, // "once" | weekly | biweekly | monthly | quarterly | yearly
    val resetMode: String,  // "reset" | "recreate"
)

@Composable
internal fun ListEditPanel(
    existing: Expense_list?,
    onClose: () -> Unit,
    onSave: (ListDraft) -> Unit,
    onDelete: (() -> Unit)?,
) {
    var name by remember(existing?.id) { mutableStateOf(existing?.name ?: "") }
    var description by remember(existing?.id) { mutableStateOf(existing?.description ?: "") }
    var status by remember(existing?.id) { mutableStateOf(existing?.status ?: "active") }
    var date by remember(existing?.id) { mutableStateOf(existing?.date ?: "") }
    var recurrence by remember(existing?.id) { mutableStateOf(existing?.recurrence ?: "once") }
    var resetMode by remember(existing?.id) { mutableStateOf(existing?.reset_mode ?: "reset") }
    val isEdit = existing != null
    val valid = name.isNotBlank()

    val fullScreen = org.teddapp.project.ui.LocalUseFullScreenForm.current
    Dialog(
        onDismissRequest = onClose,
        properties = DialogProperties(usePlatformDefaultWidth = false, dismissOnClickOutside = true),
    ) {
        Box(
            modifier = Modifier
                .fillMaxSize()
                .then(
                    if (fullScreen) Modifier.background(TeddTokens.Bg)
                    else Modifier.background(Color.Black.copy(alpha = 0.35f))
                )
                .clickable(
                    interactionSource = remember { MutableInteractionSource() },
                    indication = null,
                    onClick = onClose,
                ),
        ) {
            Column(
                modifier = Modifier
                    .then(
                        if (fullScreen) Modifier.fillMaxSize()
                        else Modifier.align(Alignment.CenterEnd).width(TeddSize.SidePanel).fillMaxHeight()
                    )
                    .background(TeddTokens.Bg)
                    .then(
                        if (fullScreen) Modifier
                        else Modifier.border(1.dp, TeddTokens.Line, RoundedCornerShape(0.dp))
                    )
                    .then(if (fullScreen) Modifier.imePadding() else Modifier)
                    .clickable(
                        interactionSource = remember { MutableInteractionSource() },
                        indication = null,
                        onClick = {},
                    ),
            ) {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .border(1.dp, TeddTokens.Line, RoundedCornerShape(0.dp))
                        .padding(horizontal = 22.dp, vertical = 16.dp),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Column(modifier = Modifier.weight(1f)) {
                        Text(
                            text = if (isEdit) "MODIFIER LA LISTE" else "NOUVELLE LISTE",
                            color = TeddTokens.InkMuted,
                            fontSize = 11.sp,
                            fontWeight = FontWeight.SemiBold,
                            letterSpacing = 0.6.sp,
                        )
                        Text(
                            text = name.ifBlank { "Sans nom" },
                            color = TeddTokens.Ink,
                            fontSize = 15.sp,
                            fontWeight = FontWeight.SemiBold,
                        )
                    }
                    Box(
                        modifier = Modifier
                            .clip(RoundedCornerShape(TeddTokens.RXs))
                            .background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RXs))
                            .clickable(
                                interactionSource = remember { MutableInteractionSource() },
                                indication = null,
                                onClick = onClose,
                            )
                            .padding(8.dp),
                    ) { TeddIcon(name = "plus", size = 14.dp, color = TeddTokens.InkMuted) }
                }

                Column(
                    modifier = Modifier
                        .weight(1f)
                        .verticalScroll(rememberScrollState())
                        .padding(22.dp),
                    verticalArrangement = Arrangement.spacedBy(16.dp),
                ) {
                    Column {
                        FieldLabel("Nom de la liste")
                        Spacer(modifier = Modifier.height(6.dp))
                        SmallTextField(value = name, onChange = { name = it }, placeholder = "Ex. Courses semaine 21")
                    }
                    Column {
                        FieldLabel("Description (optionnel)")
                        Spacer(modifier = Modifier.height(6.dp))
                        SmallTextField(value = description, onChange = { description = it }, placeholder = "Liste prête pour samedi.")
                    }
                    // Status segmented
                    Column {
                        FieldLabel("Statut")
                        Spacer(modifier = Modifier.height(6.dp))
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .clip(RoundedCornerShape(TeddTokens.RSm))
                                .background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RSm))
                                .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
                                .padding(3.dp),
                        ) {
                            listOf("active" to "Active", "planned" to "Planifiée").forEach { (k, label) ->
                                val on = status == k
                                Box(
                                    modifier = Modifier
                                        .weight(1f)
                                        .height(30.dp)
                                        .clip(RoundedCornerShape(TeddTokens.RXs))
                                        .background(if (on) TeddTokens.Surface else Color.Transparent, RoundedCornerShape(TeddTokens.RXs))
                                        .clickable(
                                            interactionSource = remember { MutableInteractionSource() },
                                            indication = null,
                                            onClick = { status = k },
                                        ),
                                    contentAlignment = Alignment.Center,
                                ) {
                                    Text(
                                        text = label,
                                        color = if (on) TeddTokens.Ink else TeddTokens.InkMuted,
                                        fontSize = 12.5.sp,
                                        fontWeight = FontWeight.Medium,
                                    )
                                }
                            }
                        }
                    }
                    Column {
                        FieldLabel("Date prévue (optionnel)")
                        Spacer(modifier = Modifier.height(6.dp))
                        SmallTextField(value = date, onChange = { date = it }, placeholder = "YYYY-MM-DD")
                    }
                    // Récurrence : si != ponctuelle, la liste se réinitialise (items
                    // repassés à non-cochés) à chaque échéance.
                    Column {
                        FieldLabel("Récurrence")
                        Spacer(modifier = Modifier.height(6.dp))
                        androidx.compose.foundation.layout.FlowRow(
                            horizontalArrangement = Arrangement.spacedBy(6.dp),
                            verticalArrangement = Arrangement.spacedBy(6.dp),
                        ) {
                            (listOf("once" to "Ponctuelle") + org.teddapp.project.db.DateMath.FREQUENCIES).forEach { (k, label) ->
                                val on = recurrence == k
                                Box(
                                    modifier = Modifier
                                        .clip(RoundedCornerShape(TeddTokens.RFull))
                                        .background(if (on) TeddTokens.LimeSoft else TeddTokens.Surface, RoundedCornerShape(TeddTokens.RFull))
                                        .border(1.dp, if (on) TeddTokens.LimeStrong else TeddTokens.Line, RoundedCornerShape(TeddTokens.RFull))
                                        .clickable(
                                            interactionSource = remember { MutableInteractionSource() },
                                            indication = null,
                                            onClick = { recurrence = k },
                                        )
                                        .padding(horizontal = 12.dp, vertical = 7.dp),
                                ) {
                                    Text(
                                        label,
                                        color = if (on) TeddTokens.LimeStrong else TeddTokens.InkMuted,
                                        fontSize = 12.5.sp,
                                        fontWeight = if (on) FontWeight.SemiBold else FontWeight.Medium,
                                    )
                                }
                            }
                        }
                        if (recurrence != "once") {
                            Spacer(modifier = Modifier.height(10.dp))
                            FieldLabel("À l'échéance")
                            Spacer(modifier = Modifier.height(6.dp))
                            Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
                                ResetModeRow(
                                    label = "Réinitialiser",
                                    sub = "La même liste se vide (items non-cochés, montants effacés).",
                                    on = resetMode == "reset",
                                    onClick = { resetMode = "reset" },
                                )
                                ResetModeRow(
                                    label = "Recréer + archiver",
                                    sub = "Un instantané daté est archivé, puis la liste se vide pour la nouvelle période.",
                                    on = resetMode == "recreate",
                                    onClick = { resetMode = "recreate" },
                                )
                            }
                        }
                    }
                }

                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .border(1.dp, TeddTokens.Line, RoundedCornerShape(0.dp))
                        .padding(16.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                ) {
                    if (onDelete != null) {
                        TeddButton(
                            text = "Supprimer",
                            kind = TeddButtonKind.Danger,
                            size = TeddButtonSize.Md,
                            onClick = onDelete,
                            leadingIcon = { TeddIcon(name = "trash", size = 13.dp, color = TeddTokens.OnAction, strokeWidth = 2.dp) },
                        )
                    }
                    Spacer(modifier = Modifier.weight(1f))
                    TeddButton(text = "Annuler", kind = TeddButtonKind.Secondary, size = TeddButtonSize.Md, onClick = onClose)
                    TeddButton(
                        text = if (isEdit) "Enregistrer" else "Créer",
                        kind = TeddButtonKind.Primary,
                        size = TeddButtonSize.Md,
                        enabled = valid,
                        onClick = {
                            onSave(
                                ListDraft(
                                    name = name.trim(),
                                    description = description.trim().ifBlank { null },
                                    status = status,
                                    date = date.trim().ifBlank { null },
                                    recurrence = recurrence,
                                    resetMode = resetMode,
                                ),
                            )
                        },
                        leadingIcon = { TeddIcon(name = "check", size = 13.dp, color = TeddTokens.OnAction, strokeWidth = 2.2.dp) },
                    )
                }
            }
        }
    }
}

/**
 * Applique la récurrence choisie à une liste après création/édition. Préserve
 * l'échéance existante si la récurrence est inchangée ; sinon cale la première
 * réinitialisation à la prochaine occurrence après aujourd'hui.
 */
internal suspend fun applyListRecurrence(
    repos: org.teddapp.project.db.TeddRepositories,
    listId: String,
    recurrence: String,
    resetMode: String,
    existing: Expense_list?,
) {
    val nextReset = when {
        recurrence == "once" -> null
        existing != null && existing.recurrence == recurrence && existing.next_reset_date != null ->
            existing.next_reset_date
        else -> org.teddapp.project.db.DateMath.step(org.teddapp.project.db.TODAY_ISO, recurrence)
    }
    repos.expenseLists.setRecurrence(listId, recurrence, resetMode, nextReset)
}

@Composable
private fun ResetModeRow(label: String, sub: String, on: Boolean, onClick: () -> Unit) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(TeddTokens.RSm))
            .background(if (on) TeddTokens.LimeSoft else TeddTokens.Surface, RoundedCornerShape(TeddTokens.RSm))
            .border(1.dp, if (on) TeddTokens.LimeStrong.copy(alpha = 0.4f) else TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
            .clickable(interactionSource = remember { MutableInteractionSource() }, indication = null, onClick = onClick)
            .padding(horizontal = 12.dp, vertical = 10.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(10.dp),
    ) {
        Box(
            modifier = Modifier.size(18.dp).clip(androidx.compose.foundation.shape.CircleShape)
                .background(if (on) TeddTokens.LimeStrong else Color.Transparent, androidx.compose.foundation.shape.CircleShape)
                .border(1.5.dp, if (on) TeddTokens.LimeStrong else TeddTokens.Line, androidx.compose.foundation.shape.CircleShape),
            contentAlignment = Alignment.Center,
        ) { if (on) TeddIcon(name = "check", size = 11.dp, color = TeddTokens.OnAction, strokeWidth = 2.6.dp) }
        Column(modifier = Modifier.weight(1f)) {
            Text(label, color = TeddTokens.Ink, fontSize = 13.sp, fontWeight = FontWeight.SemiBold)
            Text(sub, color = TeddTokens.InkMuted, fontSize = 11.sp)
        }
    }
}

@Composable
private fun FieldLabel(text: String) {
    Text(text = text, color = TeddTokens.InkMuted, fontSize = 12.sp, fontWeight = FontWeight.SemiBold)
}

@Composable
private fun SmallTextField(value: String, onChange: (String) -> Unit, placeholder: String) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .height(38.dp)
            .clip(RoundedCornerShape(TeddTokens.RSm))
            .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RSm))
            .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
            .padding(horizontal = 12.dp),
        contentAlignment = Alignment.CenterStart,
    ) {
        BasicTextField(
            value = value,
            onValueChange = onChange,
            singleLine = true,
            textStyle = TextStyle(color = TeddTokens.Ink, fontSize = 13.sp),
            cursorBrush = SolidColor(TeddTokens.LimeStrong),
            modifier = Modifier.fillMaxWidth(),
            decorationBox = { inner ->
                if (value.isEmpty()) Text(placeholder, color = TeddTokens.InkMuted, fontSize = 13.sp)
                inner()
            },
        )
    }
}
